/**
 * Modulation matrix evaluator (docs/04). Compiled once per scene change into flat arrays, then
 * evaluated every frame with no allocation:
 *
 *   effective = clamp(base + Σ amount · curve(smooth(source)) · (max − min), min, max)
 */
import {
  contribution,
  hash01,
  lfoValue,
  MOD_SOURCES,
  OSC_SOURCE,
  oscPhase,
  SOURCE_INDEX,
  type Curve,
  type OscSpec,
} from '../../shared/modulation';
import { POSTFX } from '../../shared/postfx';
import type { ParamDef } from '../../shared/params';
import { VIEW_PARAMS, type Scene, type VizLookup } from '../../shared/scene';

/** Number of fixed sources; MIDI CC sources are appended after these. */
export const FIXED_SOURCES = MOD_SOURCES.length;
export const MAX_MIDI_SOURCES = 64;

/** Registry of dynamic (MIDI CC) sources shared by all scenes. */
export class SourceRegistry {
  readonly values = new Float32Array(FIXED_SOURCES + MAX_MIDI_SOURCES);
  private readonly dynamic = new Map<string, number>();

  index(id: string): number {
    const fixed = SOURCE_INDEX.get(id);
    if (fixed !== undefined) return fixed;
    let i = this.dynamic.get(id);
    if (i === undefined) {
      if (this.dynamic.size >= MAX_MIDI_SOURCES) return -1;
      i = FIXED_SOURCES + this.dynamic.size;
      this.dynamic.set(id, i);
    }
    return i;
  }

  setDynamic(id: string, value: number): void {
    const i = this.index(id);
    if (i >= 0) this.values[i] = value;
  }
}

/** A block of parameters (a layer's params, a post-effect's params, or scalars). */
export interface ParamBlock {
  defs: readonly Pick<ParamDef, 'id' | 'min' | 'max' | 'default'>[];
  base: Float32Array;
  effective: Float32Array;
  contrib: Float32Array;
}

function block(
  defs: readonly Pick<ParamDef, 'id' | 'min' | 'max' | 'default'>[],
  values: Record<string, number> | undefined,
): ParamBlock {
  const n = defs.length;
  const base = new Float32Array(n);
  defs.forEach((d, i) => {
    const v = values?.[d.id];
    base[i] =
      typeof v === 'number' && Number.isFinite(v) ? Math.min(d.max, Math.max(d.min, v)) : d.default;
  });
  return { defs, base, effective: Float32Array.from(base), contrib: new Float32Array(n) };
}

interface CompiledMod {
  /** Index into the shared source values, or -1 for an own oscillator. */
  source: number;
  osc: OscSpec | null;
  bipolar: boolean;
  amount: number;
  curve: Curve;
  attack: number; // seconds
  release: number;
  smoothed: number;
  block: ParamBlock;
  index: number;
}

const SCALAR_DEFS = {
  opacity: { id: 'opacity', min: 0, max: 1, default: 1 },
  shift: { id: 'shift', min: 0, max: 1, default: 0 },
  intensity: { id: 'intensity', min: 0, max: 2, default: 1 },
};

export class CompiledScene {
  /**
   * Per layer (scene order): visualization params and opacity, plus the layer's own effects and
   * palette hue shift when it has its own look (null otherwise).
   */
  readonly layers: {
    id: string;
    params: ParamBlock;
    opacity: ParamBlock;
    /** Pan, zoom and sharpness (VIEW_PARAMS), applied to the layer's picture. */
    view: ParamBlock;
    fx: Map<string, ParamBlock> | null;
    paletteShift: ParamBlock | null;
  }[] = [];
  readonly fx = new Map<string, ParamBlock>();
  readonly paletteShift: ParamBlock;
  readonly intensity: ParamBlock;
  private readonly mods: CompiledMod[] = [];

  constructor(scene: Scene, viz: VizLookup, sources: SourceRegistry, defaultIntensity: number) {
    for (const layer of scene.layers) {
      const defs = viz(layer.vizId)?.params ?? [];
      let fx: Map<string, ParamBlock> | null = null;
      if (layer.postfx) {
        fx = new Map();
        for (const f of POSTFX)
          if (layer.postfx[f.id]) fx.set(f.id, block(f.params, layer.postfx[f.id]));
      }
      this.layers.push({
        id: layer.id,
        params: block(defs, layer.params),
        opacity: block([SCALAR_DEFS.opacity], { opacity: layer.opacity }),
        view: block(VIEW_PARAMS, layer.view),
        fx,
        paletteShift: layer.palette
          ? block([SCALAR_DEFS.shift], { shift: layer.paletteShift ?? 0 })
          : null,
      });
    }
    for (const fx of POSTFX) this.fx.set(fx.id, block(fx.params, scene.postfx[fx.id]));
    this.paletteShift = block([SCALAR_DEFS.shift], { shift: scene.paletteShift });
    this.intensity = block([SCALAR_DEFS.intensity], {
      intensity: scene.settings.intensity ?? defaultIntensity,
    });

    for (const m of scene.modulations) {
      const target = this.locate(m.target);
      const own = m.source === OSC_SOURCE;
      const source = own ? -1 : sources.index(m.source);
      if (!target || (!own && source < 0)) continue;
      this.mods.push({
        source,
        osc: own ? (m.osc ?? { shape: 'sine', rate: 4, sync: true }) : null,
        bipolar: m.bipolar === true,
        amount: m.amount,
        curve: m.curve,
        attack: m.attackMs / 1000,
        release: m.releaseMs / 1000,
        smoothed: 0,
        block: target.block,
        index: target.index,
      });
    }
  }

  private locate(target: string): { block: ParamBlock; index: number } | null {
    const parts = target.split('/');
    if (parts[0] === 'layer' && parts.length >= 4) {
      const l = this.layers.find((x) => x.id === parts[1]);
      if (!l) return null;
      if (parts[2] === 'palette' && parts[3] === 'shift' && l.paletteShift)
        return { block: l.paletteShift, index: 0 };
      if (parts[2] === 'view' && parts.length === 4) {
        const i = l.view.defs.findIndex((d) => d.id === parts[3]);
        return i >= 0 ? { block: l.view, index: i } : null;
      }
      if (parts[2] === 'fx' && parts.length === 5) {
        const b = l.fx?.get(parts[3]!);
        const i = b ? b.defs.findIndex((d) => d.id === parts[4]) : -1;
        return b && i >= 0 ? { block: b, index: i } : null;
      }
      return null;
    }
    if (parts[0] === 'layer' && parts.length === 3) {
      const l = this.layers.find((x) => x.id === parts[1]);
      if (!l) return null;
      if (parts[2] === 'opacity') return { block: l.opacity, index: 0 };
      const i = l.params.defs.findIndex((d) => d.id === parts[2]);
      return i >= 0 ? { block: l.params, index: i } : null;
    }
    if (parts[0] === 'fx' && parts.length === 3) {
      const b = this.fx.get(parts[1]!);
      const i = b ? b.defs.findIndex((d) => d.id === parts[2]) : -1;
      return b && i >= 0 ? { block: b, index: i } : null;
    }
    if (target === 'palette/shift') return { block: this.paletteShift, index: 0 };
    if (target === 'master/intensity') return { block: this.intensity, index: 0 };
    return null;
  }

  get modulationCount(): number {
    return this.mods.length;
  }

  /** Evaluate all modulations for this frame (`time` in seconds, `beats` continuous). */
  evaluate(sources: Float32Array, dt: number, time = 0, beats = 0): void {
    for (const m of this.mods) m.block.contrib[m.index] = 0;
    for (const m of this.mods) {
      const raw = m.osc
        ? lfoValue(m.osc.shape, oscPhase(m.osc, time, beats), hash01)
        : sources[m.source]!;
      const tau = raw > m.smoothed ? m.attack : m.release;
      m.smoothed = tau > 0 ? m.smoothed + (raw - m.smoothed) * (1 - Math.exp(-dt / tau)) : raw;
      m.block.contrib[m.index]! += contribution(m.amount, m.curve, m.smoothed, m.bipolar);
    }
    for (const m of this.mods) {
      const b = m.block;
      const i = m.index;
      const d = b.defs[i]!;
      const v = b.base[i]! + b.contrib[i]! * (d.max - d.min);
      b.effective[i] = v < d.min ? d.min : v > d.max ? d.max : v;
    }
  }

  /** Effective value of one of a layer's own effects (0 when it doesn't have that effect). */
  layerFxValue(layerIndex: number, fxId: string, paramId: string): number {
    const b = this.layers[layerIndex]?.fx?.get(fxId);
    if (!b) return 0;
    const i = b.defs.findIndex((d) => d.id === paramId);
    return i >= 0 ? b.effective[i]! : 0;
  }

  /** Effective value of a post-effect parameter. */
  fxValue(fxId: string, paramId: string): number {
    const b = this.fx.get(fxId);
    if (!b) return 0;
    const i = b.defs.findIndex((d) => d.id === paramId);
    return i >= 0 ? b.effective[i]! : 0;
  }
}
